import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserCircle, Building2, UsersRound, Mail, Loader2, ShieldCheck, PhoneCall } from "lucide-react";
import { api, type Profile, type OrganizationFull, type Member } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { MfaManager } from "@/components/security/MfaManager";
import { ChangePasswordCard } from "@/components/security/ChangePasswordCard";
import { TelephonyGoLivePanel } from "@/components/settings/TelephonyGoLivePanel";
import {
  PageHeader, Card, CardHeader, CardTitle, CardContent, Button, Input, Label,
  LoadingBlock, ErrorState, StatusBadge, PermissionDenied, Modal, toast,
} from "@/components/ds";

function ProfileSection() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["profile"], queryFn: api.getProfile });
  const [form, setForm] = useState<Partial<Profile>>({});
  const merged = { ...data, ...form } as Profile;
  const save = useMutation({
    mutationFn: () => api.updateProfile({
      first_name: merged.first_name, last_name: merged.last_name, phone: merged.phone, timezone: merged.timezone,
    }),
    onSuccess: async () => { await refresh(); qc.invalidateQueries({ queryKey: ["profile"] }); toast.success("Profile updated"); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (isLoading) return <LoadingBlock rows={3} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  return (
    <Card>
      <CardHeader><CardTitle>Your profile</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>First name</Label><Input data-testid="profile-first" value={merged.first_name ?? ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
          <div><Label>Last name</Label><Input data-testid="profile-last" value={merged.last_name ?? ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
        </div>
        <div><Label>Email</Label><Input value={merged.email ?? ""} readOnly disabled /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Phone</Label><Input data-testid="profile-phone" value={merged.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>Timezone</Label><Input data-testid="profile-tz" value={merged.timezone ?? ""} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Email status:</span>
          <StatusBadge tone={merged.email_verified ? "green" : "amber"}>{merged.email_verified ? "Verified" : "Unverified"}</StatusBadge>
          {!merged.email_verified && (
            <Button variant="ghost" size="sm" data-testid="resend-verification" onClick={async () => { try { await api.resendVerification(); toast.success("Verification link sent (see server log — email not configured)"); } catch (e) { toast.error(formatApiError(e)); } }}>Resend link</Button>
          )}
        </div>
        <Button data-testid="save-profile" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save profile
        </Button>
      </CardContent>
    </Card>
  );
}

function OrganizationSection() {
  const { role } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["organization"], queryFn: api.getOrganization });
  const [form, setForm] = useState<Partial<OrganizationFull>>({});
  const org = { ...data?.organization, ...form } as OrganizationFull;
  const save = useMutation({
    mutationFn: () => api.updateOrganization({
      name: org.name, legal_name: org.legal_name, website: org.website,
      primary_email: org.primary_email, primary_phone: org.primary_phone, timezone: org.timezone, industry: org.industry,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["organization"] }); toast.success("Organization updated"); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (isLoading) return <LoadingBlock rows={3} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const isOwner = role === "owner";
  return (
    <Card>
      <CardHeader><CardTitle>Organization settings</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {!isOwner && <PermissionDenied message="Only the organization owner can edit these settings. You can view them below." />}
        <div><Label>Business name</Label><Input data-testid="org-name" value={org.name ?? ""} disabled={!isOwner} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Industry</Label><Input data-testid="org-industry" value={org.industry ?? ""} disabled={!isOwner} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
          <div><Label>Timezone</Label><Input value={org.timezone ?? ""} disabled={!isOwner} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Primary email</Label><Input value={org.primary_email ?? ""} disabled={!isOwner} onChange={(e) => setForm({ ...form, primary_email: e.target.value })} /></div>
          <div><Label>Primary phone</Label><Input value={org.primary_phone ?? ""} disabled={!isOwner} onChange={(e) => setForm({ ...form, primary_phone: e.target.value })} /></div>
        </div>
        <div><Label>Website</Label><Input value={org.website ?? ""} disabled={!isOwner} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
        {isOwner && (
          <Button data-testid="save-org" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save organization
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function TeamSection() {
  const { role } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["members"], queryFn: api.getMembers });
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const invite = useMutation({
    mutationFn: () => api.invite(email, "staff"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["members"] }); setOpen(false); setEmail(""); toast.success("Invitation recorded (email delivery not configured)"); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (isLoading) return <LoadingBlock rows={2} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const isOwner = role === "owner";
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Team</CardTitle>
        {isOwner && <Button size="sm" data-testid="invite-btn" onClick={() => setOpen(true)}>Invite staff</Button>}
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-slate-100">
          {(data as Member[]).map((m) => (
            <div key={m.id} className="flex items-center justify-between py-3" data-testid={`member-${m.id}`}>
              <div>
                <p className="text-sm font-medium text-slate-900">
                  {m.profile?.first_name ? `${m.profile.first_name} ${m.profile.last_name ?? ""}` : (m.invited_email || m.profile?.email)}
                </p>
                <p className="text-xs text-slate-500">{m.profile?.email || m.invited_email}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge tone={m.role === "owner" ? "blue" : "slate"}>{m.role}</StatusBadge>
                <StatusBadge tone={m.invitation_status === "accepted" ? "green" : "amber"}>{m.invitation_status}</StatusBadge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <Modal open={open} onOpenChange={setOpen} title="Invite a staff member" description="They'll be added as pending. Email delivery is not configured yet." testid="invite-modal">
        <div className="space-y-3">
          <div><Label>Email</Label><Input type="email" data-testid="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <Button className="w-full" data-testid="invite-submit" disabled={!email || invite.isPending} onClick={() => invite.mutate()}>
            {invite.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Send invitation
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

const TABS = [
  { key: "profile", label: "Profile", icon: UserCircle },
  { key: "organization", label: "Organization", icon: Building2 },
  { key: "team", label: "Team", icon: UsersRound },
  { key: "security", label: "Security", icon: ShieldCheck },
  { key: "telephony", label: "Telephony", icon: PhoneCall },
];

export default function Settings() {
  const [tab, setTab] = useState("profile");
  return (
    <div data-testid="page-settings">
      <PageHeader title="Settings" description="Manage your profile, organization and team." />
      <div className="mb-6 flex gap-1 border-b border-slate-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`settings-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "profile" && <ProfileSection />}
      {tab === "organization" && <OrganizationSection />}
      {tab === "team" && <TeamSection />}
      {tab === "security" && (
        <div className="space-y-6" data-testid="security-section">
          <MfaManager />
          <ChangePasswordCard />
        </div>
      )}
      {tab === "telephony" && (
        <div className="space-y-6" data-testid="telephony-section">
          <TelephonyGoLivePanel />
        </div>
      )}
    </div>
  );
}
